import React, { useCallback, useRef, useImperativeHandle, forwardRef } from "react"
import Card from "../cards/Card"
import { Swiper as SwiperClass } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import type { MapCardsProps } from "../../types"
import "./styles/MapCards.scss"
import "swiper/scss"
import "swiper/scss/navigation"
import type { SwiperOptions } from "swiper/types"
import { PLACE_DEFAULT_IMAGE } from "./constants"
import { Navigation } from "swiper/modules"

const MapCards = forwardRef<{ slideTo: (index: number) => void }, MapCardsProps>(({ places, onClick, onFocus }, ref) => {
  const swiperRef = useRef<SwiperClass | null>(null)

  useImperativeHandle(ref, () => ({
    slideTo: (index: number) => {
      if (swiperRef.current) {
        swiperRef.current.slideTo(index)
      }
    },
  }))

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      const activeIndex = swiper.realIndex
      const currentPlace = places[activeIndex]

      if (currentPlace && onFocus) {
        onFocus(currentPlace)
      }
    },
    [places, onFocus]
  )

  const swiperParams: SwiperOptions = {
    modules: [Navigation],
    slidesPerView: 1,
    spaceBetween: 0,
    direction: "horizontal",
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    on: {
      slideChange: handleSlideChange,
      activeIndexChange: handleSlideChange,
      init: function (swiper) {
        handleSlideChange(swiper)
      },
    },
    loop: false,
    speed: 500,
    watchSlidesProgress: true,
  }

  return (
    <div className="map-carousel">
      <div className="map-cards">
        <button className="swiper-button swiper-button-prev" aria-label="Previous slide">
          <svg viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button className="swiper-button swiper-button-next" aria-label="Next slide">
          <svg viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
          </svg>
        </button>
        <Swiper
          {...swiperParams}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            handleSlideChange(swiper)
          }}
        >
          {places.map((place) => (
            <SwiperSlide key={place.id}>
              <Card
                id={place.id}
                title={place.properties.name}
                image={place.properties.image || place.properties.thumbnail_url || PLACE_DEFAULT_IMAGE}
                categories={place.properties.categories}
                vibes={place.properties.vibes?.slice(0, 3)}
                orientation="horizontal"
                size="small"
                showVibes={true}
                onCardClick={() => onClick(place)}
                onMouseEnter={() => onFocus?.(place)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
})

export default React.memo(MapCards)
